import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'cart_items_v1';

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const addToCart = (product, quantity = 1) => {
    if (!product || !product.id) return;
    setItems(prev => {
      const idx = prev.findIndex(p => p.id === product.id);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          quantity: (next[idx].quantity || 0) + quantity,
        };
        return next;
      }
      const normalized = {
        id: product.id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        image: product.image,
        category: product.category,
        variant: product.variant,
        stock: product.stock ?? 10,
        quantity: quantity,
      };
      return [...prev, normalized];
    });
  };

  const removeFromCart = (id) => {
    setItems(prev => prev.filter(p => p.id !== id));
  };

  const updateQuantity = (id, quantity) => {
    setItems(prev => prev.map(p => p.id === id ? { ...p, quantity: Math.max(1, quantity) } : p));
  };

  const clearCart = () => setItems([]);

  const value = useMemo(() => ({ items, addToCart, removeFromCart, updateQuantity, clearCart }), [items]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
